import { Reveal } from "./motion";

const KillsIt = ({ children }) => (
  <div className="mt-6 border-l-4 border-ink bg-cloud p-4">
    <p className="mb-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50">
      What kills it
    </p>
    <p className="text-base leading-relaxed text-ink/80">{children}</p>
  </div>
);

const StepShell = ({ num, title, subtitle, children }) => (
  <Reveal>
    <div className="grid grid-cols-1 gap-6 border-2 border-ink bg-white p-7 md:grid-cols-12 md:gap-10 md:p-10" data-testid={`step-${num}`}>
      <div className="md:col-span-4">
        <span className="font-display text-6xl font-black leading-none text-ink/15 md:text-7xl">{num}</span>
        <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink md:text-3xl">
          {title}
        </h3>
        <p className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.16em] text-acid" style={{ WebkitTextStroke: "0.3px #0A1128" }}>
          {subtitle}
        </p>
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  </Reveal>
);

const askQuestions = [
  "What brought you here?",
  "What are you working on right now?",
  "Why does that matter to you?",
  "What are you excited about?",
  "Who are you hoping to meet?",
];

const listenFor = ["Passion", "Priorities", "Frustrations", "Goals and values", "Opportunities to support"];

export const FiveSteps = () => {
  return (
    <section className="bg-cloud py-20 md:py-28" data-testid="five-steps-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.22em] text-ink/50">The shape</p>
              <h2 className="font-display text-4xl font-black uppercase tracking-tight text-ink sm:text-5xl lg:text-6xl">
                Five steps.
              </h2>
            </div>
            <p className="max-w-sm text-base leading-relaxed text-ink/60">
              Not a script. Not five lines to recite. Five decisions that keep the conversation human and
              intentional.
            </p>
          </div>
        </Reveal>

        <div className="flex flex-col gap-6">
          <StepShell num="01" title="Set your intention" subtitle="Know why you are in the room">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              Before the event, decide why you are entering the room. Your primary intention may be to
              learn, connect, serve, collaborate, or grow.
            </p>
            <KillsIt>
              Walking in thinking, &ldquo;I need to find three clients.&rdquo; That turns every person into an
              opportunity to evaluate instead of a person to understand.
            </KillsIt>
          </StepShell>

          <StepShell num="02" title="Start warm" subtitle="Be a person first">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">A simple greeting is enough:</p>
            <p className="mt-4 border-2 border-ink bg-white p-5 font-display text-xl font-bold text-ink md:text-2xl">
              &ldquo;Hey, I&apos;m Jason. It&apos;s great to meet you. What brings you here?&rdquo;
            </p>
            <KillsIt>
              The elevator pitch. The second you lead with a memorized commercial, the conversation becomes
              about you.
            </KillsIt>
          </StepShell>

          <StepShell num="03" title="Make it about them" subtitle="Ask and listen">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              Ask questions that let them share who they are, why they came, and what matters to them.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="border-2 border-ink bg-white p-5">
                <p className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-ink/50">
                  Ask to understand
                </p>
                <ul className="space-y-2">
                  {askQuestions.map((q) => (
                    <li key={q} className="flex gap-2 text-sm text-ink md:text-base">
                      <span className="text-acid" style={{ WebkitTextStroke: "0.3px #0A1128" }}>&rsaquo;</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-2 border-ink bg-ink p-5 text-white">
                <p className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-cyan">
                  Listen beyond the words
                </p>
                <ul className="space-y-2">
                  {listenFor.map((q) => (
                    <li key={q} className="flex gap-2 text-sm text-white/85 md:text-base">
                      <span className="text-acid">&rsaquo;</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-5 font-display text-xl font-extrabold uppercase leading-tight tracking-tight text-ink md:text-2xl">
              Your first job is not to be interesting.
              <br />
              <span className="text-acid" style={{ WebkitTextStroke: "0.5px #0A1128" }}>Your first job is to be interested.</span>
            </p>
            <KillsIt>
              Listening only for a problem your business can solve. That is not listening. That is waiting for
              your turn to pitch.
            </KillsIt>
          </StepShell>

          <StepShell num="04" title="Keep it simple" subtitle="When they ask about you">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              Eventually, they may ask, &ldquo;What do you do?&rdquo; That is not an invitation to give a speech.
              Answer the question they asked, then stop.
            </p>
            <p className="mt-4 border-2 border-ink bg-white p-5 font-display text-lg font-bold text-ink md:text-xl">
              &ldquo;I help business owners get better at sales and networking without relying on pitches.&rdquo;
            </p>
            <KillsIt>
              Answering the next five questions they have not asked. A short answer creates dialogue. A long
              answer takes control of the conversation.
            </KillsIt>
          </StepShell>

          <StepShell num="05" title="Create the next step" subtitle="Move the right relationship forward">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              A meaningful conversation should not end with &ldquo;We should connect sometime.&rdquo; Decide whether
              the relationship has somewhere to go.
            </p>
            <ul className="mt-4 space-y-2">
              {[
                "Send a specific resource or make a valuable introduction",
                "Invite them to an event, community, or useful conversation",
                "Schedule a 30-minute Deeper Conversation",
              ].map((q) => (
                <li key={q} className="flex gap-2 text-base text-ink md:text-lg">
                  <span className="text-acid" style={{ WebkitTextStroke: "0.3px #0A1128" }}>&rsaquo;</span> {q}
                </li>
              ))}
            </ul>
            <div className="mt-6 border-2 border-ink bg-ink p-5 text-white">
              <p className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-cyan">
                Follow-up proves you listened
              </p>
              <p className="text-sm leading-relaxed text-white/85 md:text-base">
                Within 24 hours: follow up personally. Within three days: deliver what you promised. Within
                seven days: complete the next step. Within 30 days: reconnect with relevance.
              </p>
            </div>
          </StepShell>
        </div>
      </div>
    </section>
  );
};
